import React from "react";
import styled, { keyframes, css } from "styled-components";

/*
 * Skeleton sistem, zamena za generičke spinnere (v2 spec 4.5).
 * Svaki kompozit prati oblik finalnog layouta stranice na kojoj se koristi,
 * pa nema layout shift-a kad sadržaj stigne. Boje isključivo iz tokena.
 *
 * Kompoziti:
 *  - portal: ProductGridSkeleton, BandsGridSkeleton, NewsListSkeleton,
 *            ProductDetailSkeleton, HeroDetailSkeleton, ArticleSkeleton
 *  - admin:  TableSkeleton, StatSkeleton, StatGridSkeleton, ChartSkeleton,
 *            FormSkeleton, DetailSkeleton
 *  - shell:  PageSkeleton (route fallback), PanelSkeleton (bend/admin panel)
 */

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const boneBase = css`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.05) 25%,
    rgba(255, 255, 255, 0.1) 37%,
    rgba(255, 255, 255, 0.05) 63%
  );
  background-size: 400% 100%;
  animation: ${shimmer} 1.4s ease infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    background: rgba(255, 255, 255, 0.06);
  }
`;

/* Osnovna "koska", dimenzije preko $w/$h/$r/$mb propova */
export const Bone = styled.div`
  ${boneBase};
  width: ${(p) => p.$w || "100%"};
  height: ${(p) => p.$h || "14px"};
  border-radius: ${(p) => p.$r || "6px"};
  ${(p) => (p.$mb ? `margin-bottom: ${p.$mb};` : "")}
  flex-shrink: 0;
`;

/* Deterministične širine linija teksta, bez Math.random da render bude stabilan */
const LINE_WIDTHS = ["92%", "68%", "84%", "75%", "88%", "60%", "80%", "71%"];
const lineWidth = (i) => LINE_WIDTHS[i % LINE_WIDTHS.length];

const srStatus = { role: "status", "aria-label": "Učitavanje sadržaja" };

/* ===================================================================== */
/* Portal                                                                */
/* ===================================================================== */

/* Grid proizvoda, prati ProductCard (slika 1:1, bend, naziv u 2 reda, cena)
   i Grid iz prodavnice (auto-fill 250px, 2 kolone na mobilnom) */

const SkProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(${(p) => p.$min}px, 1fr));
  gap: 24px;

  @media (max-width: 560px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const SkCard = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
`;

const SkCardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.9rem 1rem 1rem;
`;

export const ProductGridSkeleton = ({ count = 8, min = 250 }) => (
  <SkProductGrid $min={min} {...srStatus}>
    {Array.from({ length: count }, (_, i) => (
      <SkCard key={i}>
        <Bone $h="auto" $r="0" style={{ aspectRatio: "1 / 1" }} />
        <SkCardBody>
          <Bone $w="38%" $h="10px" />
          <Bone $w={lineWidth(i)} $h="14px" />
          <Bone $w="55%" $h="14px" />
          <Bone $w="40%" $h="16px" style={{ marginTop: "0.35rem" }} />
        </SkCardBody>
      </SkCard>
    ))}
  </SkProductGrid>
);

/* Grid bendova/festivala, 16:9 kartice sa overlay tekstom pri dnu */

const SkBandsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 1000px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SkBandCard = styled.div`
  ${boneBase};
  position: relative;
  aspect-ratio: 16 / 9;
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
`;

const SkBandOverlay = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  padding: 1rem 1.1rem;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.55));

  > div {
    animation: none;
    background: rgba(255, 255, 255, 0.13);
  }
`;

export const BandsGridSkeleton = ({ count = 6 }) => (
  <SkBandsGrid {...srStatus}>
    {Array.from({ length: count }, (_, i) => (
      <SkBandCard key={i}>
        <SkBandOverlay>
          <Bone $w={i % 2 ? "45%" : "58%"} $h="20px" />
          <Bone $w="72%" $h="11px" />
        </SkBandOverlay>
      </SkBandCard>
    ))}
  </SkBandsGrid>
);

/* Novosti, istaknuta kartica (slika levo 50%) + uniformni 3-kolonski grid */

const SkFeatured = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
  margin-bottom: 2rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const SkFeaturedImage = styled(Bone)`
  min-height: 320px;
  height: 100%;
  border-radius: 0;

  @media (max-width: 900px) {
    aspect-ratio: 16 / 10;
    min-height: 0;
    height: auto;
  }
`;

const SkFeaturedBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 2rem;

  @media (max-width: 900px) {
    padding: 1.4rem;
  }
`;

export const NewsListSkeleton = ({ count = 6 }) => (
  <div {...srStatus}>
    <SkFeatured>
      <SkFeaturedImage $h="auto" />
      <SkFeaturedBody>
        <Bone $w="120px" $h="22px" $r="var(--radius-pill)" />
        <Bone $w="90%" $h="26px" style={{ marginTop: "0.4rem" }} />
        <Bone $w="65%" $h="26px" />
        <Bone $w="95%" $h="13px" style={{ marginTop: "0.4rem" }} />
        <Bone $w="88%" $h="13px" />
        <Bone $w="60%" $h="13px" />
        <Bone $w="140px" $h="40px" $r="var(--radius-control)" style={{ marginTop: "auto" }} />
      </SkFeaturedBody>
    </SkFeatured>

    <SkBandsGrid>
      {Array.from({ length: count }, (_, i) => (
        <SkCard key={i}>
          <Bone $h="auto" $r="0" style={{ aspectRatio: "16 / 10" }} />
          <SkCardBody>
            <Bone $w="50%" $h="10px" />
            <Bone $w={lineWidth(i)} $h="15px" />
            <Bone $w="62%" $h="15px" />
          </SkCardBody>
        </SkCard>
      ))}
    </SkBandsGrid>
  </div>
);

/* Detalj proizvoda, galerija 55% (sticky) + info kolona 45% */

const SkProductLayout = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 55fr) minmax(0, 45fr);
  gap: 3rem;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const SkThumbRail = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`;

const SkSizeRow = styled.div`
  display: flex;
  gap: 10px;
`;

export const ProductDetailSkeleton = () => (
  <div {...srStatus}>
    <Bone $w="260px" $h="13px" $mb="1.5rem" />
    <SkProductLayout>
      <div>
        <Bone $h="auto" $r="var(--radius-card)" style={{ aspectRatio: "1 / 1" }} />
        <SkThumbRail>
          {Array.from({ length: 4 }, (_, i) => (
            <Bone key={i} $w="64px" $h="64px" $r="8px" />
          ))}
        </SkThumbRail>
      </div>
      <div>
        <Bone $w="120px" $h="24px" $r="var(--radius-pill)" $mb="1rem" />
        <Bone $w="85%" $h="30px" $mb="0.6rem" />
        <Bone $w="55%" $h="30px" $mb="1.4rem" />
        <Bone $w="35%" $h="26px" $mb="2rem" />
        <Bone $w="80px" $h="12px" $mb="0.8rem" />
        <SkSizeRow>
          {Array.from({ length: 4 }, (_, i) => (
            <Bone key={i} $w="52px" $h="42px" $r="var(--radius-control)" />
          ))}
        </SkSizeRow>
        <Bone $h="52px" $r="var(--radius-control)" style={{ marginTop: "1.6rem" }} />
        <Bone $w="90%" $h="13px" style={{ marginTop: "2rem" }} />
        <Bone $w="75%" $h="13px" style={{ marginTop: "0.6rem" }} />
        <Bone $w="82%" $h="13px" style={{ marginTop: "0.6rem" }} />
      </div>
    </SkProductLayout>
  </div>
);

/* Detalj benda/festivala, full-bleed hero (naziv, meta, social) + bio linije */

const SkHero = styled.div`
  ${boneBase};
  position: relative;
  min-height: 40vh;
  border-radius: 0;
  display: flex;
  align-items: flex-end;
`;

const SkHeroInner = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  > div {
    animation: none;
    background: rgba(255, 255, 255, 0.13);
  }
`;

const SkSocialRow = styled.div`
  display: flex;
  gap: 10px;
`;

const SkHeroContent = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem;
`;

export const HeroDetailSkeleton = () => (
  <div {...srStatus}>
    <SkHero>
      <SkHeroInner>
        <Bone $w="min(420px, 60%)" $h="42px" />
        <Bone $w="min(300px, 45%)" $h="14px" />
        <SkSocialRow>
          {Array.from({ length: 4 }, (_, i) => (
            <Bone key={i} $w="38px" $h="38px" $r="50%" />
          ))}
        </SkSocialRow>
      </SkHeroInner>
    </SkHero>
    <SkHeroContent>
      <Bone $w="60px" $h="12px" $mb="1.2rem" />
      {Array.from({ length: 4 }, (_, i) => (
        <Bone key={i} $w={lineWidth(i)} $h="14px" $mb="0.7rem" />
      ))}
    </SkHeroContent>
  </div>
);

/* Članak (detalj novosti), naslov, meta, hero slika 16:9, pasusi */

const SkArticle = styled.div`
  max-width: 840px;
  margin: 0 auto;
`;

const SkMetaRow = styled.div`
  display: flex;
  gap: 12px;
  margin: 1rem 0 1.5rem;
`;

export const ArticleSkeleton = () => (
  <SkArticle {...srStatus}>
    <Bone $w="220px" $h="13px" $mb="1.5rem" />
    <Bone $w="95%" $h="34px" $mb="0.7rem" />
    <Bone $w="60%" $h="34px" />
    <SkMetaRow>
      <Bone $w="90px" $h="22px" $r="var(--radius-pill)" />
      <Bone $w="120px" $h="22px" $r="var(--radius-pill)" />
    </SkMetaRow>
    <Bone $h="auto" $r="var(--radius-card)" $mb="2rem" style={{ aspectRatio: "16 / 9" }} />
    {Array.from({ length: 7 }, (_, i) => (
      <Bone key={i} $w={lineWidth(i)} $h="14px" $mb="0.75rem" />
    ))}
  </SkArticle>
);

/* ===================================================================== */
/* Admin / bend panel                                                    */
/* ===================================================================== */

/* Tabela, header traka + redovi sa ćelijama različitih širina.
   `bare` varijanta bez sopstvene kartice, za upotrebu unutar postojećeg
   TableContainer-a ili <td colSpan> ćelije. */

const CELL_WIDTHS = [
  ["12%", "24%", "16%", "9%", "11%", "8%"],
  ["9%", "20%", "18%", "11%", "13%", "10%"],
  ["14%", "22%", "14%", "9%", "9%", "12%"],
];

const SkTableCardWrap = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  overflow: hidden;
`;

const SkTableHead = styled.div`
  display: flex;
  gap: 3%;
  padding: 0.9rem 1.25rem;
  background: var(--bg-surface-2);

  > div {
    animation: none;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const SkTableRow = styled.div`
  display: flex;
  align-items: center;
  gap: 3%;
  padding: 1.05rem 1.25rem;

  & + & {
    border-top: 1px solid var(--border);
  }
`;

export const TableSkeleton = ({ rows = 6, cols = 6, bare = false }) => {
  const colCount = Math.min(cols, 6);
  const body = (
    <>
      <SkTableHead>
        {Array.from({ length: colCount }, (_, c) => (
          <Bone key={c} $w={CELL_WIDTHS[0][c]} $h="11px" />
        ))}
      </SkTableHead>
      {Array.from({ length: rows }, (_, r) => (
        <SkTableRow key={r}>
          {Array.from({ length: colCount }, (_, c) => (
            <Bone key={c} $w={CELL_WIDTHS[r % CELL_WIDTHS.length][c]} $h="13px" />
          ))}
        </SkTableRow>
      ))}
    </>
  );

  return bare ? (
    <div {...srStatus} style={{ textAlign: "initial" }}>
      {body}
    </div>
  ) : (
    <SkTableCardWrap {...srStatus}>{body}</SkTableCardWrap>
  );
};

/* Stat kartica, vrednost + label linije (bez sopstvene kartice,
   ubacuje se u postojeći StatCard) */

const SkStatWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  width: 100%;
`;

export const StatSkeleton = ({ lines = 2 }) => (
  <SkStatWrap {...srStatus}>
    <Bone $w="70%" $h="26px" />
    {Array.from({ length: Math.max(lines - 1, 1) }, (_, i) => (
      <Bone key={i} $w={i % 2 ? "60%" : "85%"} $h="12px" />
    ))}
  </SkStatWrap>
);

/* Grid stat kartica, kad se skeletira cela sekcija statistike */

const SkStatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const SkStatCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-card);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
`;

export const StatGridSkeleton = ({ cards = 3, lines = 2 }) => (
  <SkStatGrid {...srStatus}>
    {Array.from({ length: cards }, (_, i) => (
      <SkStatCard key={i}>
        <Bone $w="40px" $h="40px" $r="50%" />
        <StatSkeleton lines={lines} />
      </SkStatCard>
    ))}
  </SkStatGrid>
);

/* Graf, vertikalne trake deterministične visine; `donut` varijanta krug */

const BAR_HEIGHTS = [62, 38, 74, 50, 86, 44, 68, 58, 80, 35, 72, 55];

const SkChartBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 10px;
  width: 100%;
  height: ${(p) => p.$h}px;
  padding-top: 12px;
`;

const SkBar = styled(Bone)`
  flex: 1;
  border-radius: 4px 4px 0 0;
`;

const SkDonutWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${(p) => p.$h}px;
`;

export const ChartSkeleton = ({ height = 300, donut = false }) =>
  donut ? (
    <SkDonutWrap $h={height} {...srStatus}>
      <Bone $w="180px" $h="180px" $r="50%" />
    </SkDonutWrap>
  ) : (
    <SkChartBars $h={height} {...srStatus}>
      {BAR_HEIGHTS.map((h, i) => (
        <SkBar key={i} $h={`${h}%`} $w="auto" />
      ))}
    </SkChartBars>
  );

/* Forma, parovi label + input, za stranice izmene */

const SkFormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
`;

export const FormSkeleton = ({ fields = 6 }) => (
  <SkFormWrap {...srStatus}>
    {Array.from({ length: fields }, (_, i) => (
      <div key={i}>
        <Bone $w={i % 2 ? "140px" : "110px"} $h="12px" $mb="0.6rem" />
        <Bone $h={i === fields - 1 ? "96px" : "42px"} $r="var(--radius-control)" />
      </div>
    ))}
    <Bone $w="150px" $h="42px" $r="var(--radius-control)" />
  </SkFormWrap>
);

/* Detalji (porudžbina, email log, modal), red label levo + vrednost desno */

const SkDetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 2rem;
  padding: 0.85rem 0;

  & + & {
    border-top: 1px solid var(--border);
  }
`;

export const DetailSkeleton = ({ lines = 8 }) => (
  <div {...srStatus} style={{ textAlign: "initial" }}>
    <Bone $w="40%" $h="20px" $mb="1.2rem" />
    {Array.from({ length: lines }, (_, i) => (
      <SkDetailRow key={i}>
        <Bone $w={i % 2 ? "22%" : "28%"} $h="13px" />
        <Bone $w={lineWidth(i)} $h="13px" style={{ maxWidth: "42%" }} />
      </SkDetailRow>
    ))}
  </div>
);

/* ===================================================================== */
/* App shell                                                             */
/* ===================================================================== */

/* Route fallback, neutralni oblik stranice dok se chunk skida */

const SkPage = styled.div`
  ${(p) => (p.$full ? "min-height: 100dvh; background: var(--bg-app);" : "")}
`;

const SkPageInner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 6.5rem 1.5rem 3rem;
`;

export const PageSkeleton = ({ full = false }) => (
  <SkPage $full={full} {...srStatus}>
    <SkPageInner>
      <Bone $w="90px" $h="12px" $mb="1rem" />
      <Bone $w="min(320px, 70%)" $h="44px" $mb="2.5rem" />
      <ProductGridSkeleton count={4} />
    </SkPageInner>
  </SkPage>
);

/* Bend/admin panel, sidebar traka + sadržaj, dok se sesija proverava */

const SkPanel = styled.div`
  display: flex;
  min-height: 100dvh;
  background: var(--bg-app);
`;

const SkSidebar = styled.div`
  width: 220px;
  flex-shrink: 0;
  background: var(--bg-sidebar);
  border-right: 1px solid var(--border);
  padding: 1.5rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const SkPanelContent = styled.div`
  flex: 1;
  min-width: 0;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

export const PanelSkeleton = () => (
  <SkPanel {...srStatus}>
    <SkSidebar>
      <Bone $w="120px" $h="28px" $mb="1.5rem" />
      {Array.from({ length: 6 }, (_, i) => (
        <Bone key={i} $h="36px" $r="var(--radius-control)" />
      ))}
    </SkSidebar>
    <SkPanelContent>
      <Bone $w="min(260px, 60%)" $h="30px" />
      <StatGridSkeleton cards={4} />
      <TableSkeleton rows={5} />
    </SkPanelContent>
  </SkPanel>
);
